import React from "react";
import ServicePage from "./ServicePage";

export default function WebApps() {
  return (
    <ServicePage
      slug="web-apps"
      eyebrow="Service · 02"
      title="Full-Stack"
      titleAccent="Web Apps."
      intro="Production-grade React, Next.js and React Native apps with a real backend behind them. Strong UX, accessibility, and a componentized design system that doesn't decay six months in."
      capabilities={[
        {
          title: "Web app from zero",
          desc: "From an empty repo to a deployed v1 — auth, billing, data layer, design system, CI. Built with the boring, durable choices that hold up.",
          points: [
            "Routing, auth, billing wired in early (not bolted on later)",
            "Design tokens and componentized UI from day one",
            "Observability: logs, errors, basic metrics from launch",
          ],
        },
        {
          title: "Frontend rebuilds",
          desc: "Replace a tired SPA with Next.js or a modern React stack. Incremental migration when possible — strangle the old shell page by page.",
          points: [
            "Route-by-route migration (no big-bang)",
            "Shared design system across old + new",
            "Telemetry to prove no regression in conversion",
          ],
        },
        {
          title: "Mobile (React Native)",
          desc: "Cross-platform apps that share business logic with the web. Realistic about when native is the right call instead.",
          points: [
            "Expo or bare RN depending on native needs",
            "Shared types + API layer with the web",
            "Native modules where they actually pay off",
          ],
        },
        {
          title: "Design system + component library",
          desc: "A small, sharp set of primitives that compose. Docs, Storybook, accessibility audited — built to be used, not admired.",
          points: [
            "Tokens (color, spacing, type) wired to Tailwind / CSS vars",
            "Storybook with usage examples and a11y notes",
            "Migration plan from any current ad-hoc CSS",
          ],
        },
      ]}
      process={[
        { title: "Discovery", desc: "Goals, users, constraints. What's truly unique vs. what's table stakes." },
        { title: "Spike + plan", desc: "A small prototype to de-risk the unknowns. A real timeline, not a wish." },
        { title: "Iterative build", desc: "Two-week slices, each one shippable. Demos every Friday." },
        { title: "Launch & iterate", desc: "Beta cohort, telemetry, a written list of what's intentionally not in v1." },
      ]}
      outcomes={[
        { kpi: "Velocity", title: "Predictable shipping", desc: "Weekly demos, not vague status reports." },
        { kpi: "Quality", title: "Accessibility built-in", desc: "WCAG-grade UI, keyboard navigation, focus states, screen-reader friendly." },
        { kpi: "Maintainability", title: "Hand-off ready", desc: "Docs, tests and a clear ownership map so your team can run with it." },
      ]}
      techHighlights={[
        "React 19 / Server Components",
        "Next.js (App Router)",
        "TypeScript",
        "Tailwind / CSS variables",
        "React Native / Expo",
        "Zustand / TanStack Query",
        "Vitest / Playwright",
        "Storybook",
      ]}
      faqs={[
        {
          q: "Next.js vs. plain React (Vite/CRA)?",
          a: "Default to Next.js for anything user-facing — SSR, image pipeline, routing and middleware all matter for performance and SEO. Plain Vite-style React fits better for embedded apps, internal tools or extension-style UIs where SSR isn't useful.",
        },
        {
          q: "Do you ship tests?",
          a: "Yes — but pragmatically. Critical paths get end-to-end coverage with Playwright. Pure logic gets unit tests. UI snapshots are usually a waste; visual regression on a few canonical screens is more honest.",
        },
        {
          q: "Can you take over an existing codebase?",
          a: "Most of my work is exactly that. I do a code reading session, write a short audit (debt, risks, low-hanging wins), and propose a path that doesn't require a rewrite.",
        },
        {
          q: "What about backend?",
          a: "Often the same engagement — see the API & Backend page. For app-only work I integrate cleanly with your existing services.",
        },
      ]}
    />
  );
}
